import { ApolloProvider } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { useIsNativeApp } from '../../../../front/src/hooks/useIsNativeApp';
import { AuthProvider } from '../../../../front/src/hooks/usePermissions';
import { useApollo } from '../../../../front/src/services/apollo/initApollo';
import { gcAuthInterceptor } from '../../../../front/src/services/axios';
import { AuthWithPromiseProvider } from '../../../../front/src/utils/graphlogin/AuthWithPromiseProvider';
import { SEMIO_PRODUCT } from '../../utils/consts';
import { useLanguage } from '../context/LanguageContext';
import { SubscribeToTokenProvider } from './HasTokenProvider';
import { getGraphLogin } from './LoadGraphLogin';

type AuthManagerProps = {
  pageProps: any;
  publicKey: string;
};

export const AuthManager: React.FC<PropsWithChildren<AuthManagerProps>> = memo(
  function AuthManager({ pageProps, publicKey, children }) {
    const { currentLanguage } = useLanguage();
    const isNative = useIsNativeApp();
    const { apolloClient, auth, getToken } = useApolloInBrowser(
      pageProps,
      publicKey,
      currentLanguage,
    );

    // Handle modal resize in native environment
    useEffect(() => {
      if (!isNative) return;

      const handleResize = () => {
        // Force modal to take full screen in native environment
        const modal = document.querySelector('.graphlogin-modal');
        if (modal) {
          modal.classList.add('native-modal');
        }
      };

      // Initial resize
      handleResize();

      // Listen for orientation changes
      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
      };
    }, [isNative]);

    return (
      <SubscribeToTokenProvider getToken={getToken}>
        <ApolloProvider client={apolloClient}>
          <AuthProvider graphLogin={auth}>
            <AuthWithPromiseProvider>{children}</AuthWithPromiseProvider>
          </AuthProvider>
        </ApolloProvider>
      </SubscribeToTokenProvider>
    );
  },
);

function useApolloInBrowser(pageProps: any, publicKey: string, locale: string) {
  const auth = useMemo(() => getGraphLogin(publicKey, locale), [locale, publicKey]);
  const { getToken, subscribeToToken, fetchGraphLogin, logout } = auth;

  const getPublicKey = useCallback(() => publicKey, [publicKey]);

  const triggerRefreshToken = useCallback(
    () => fetchGraphLogin('triggerRefreshToken'),
    [fetchGraphLogin],
  );

  const apolloClient = useApollo(
    // product: 'graphdebate' (B0_02b) → toutes les requêtes GraphQL du forum portent semio-product
    // → l'API marque first_connected.graphdebate. graphlogin ne le passe pas (pas de pollution).
    { getPublicKey, getToken, triggerRefreshToken, logout, product: SEMIO_PRODUCT },
    pageProps,
  );

  // Resetting the apollo store on all token change causes all kinds of bugs and side effects, with the store being reset while a gql query is in progress.
  // Let's reset only when logging out, i.e. no token. So that we don't reset when the token is refreshed.
  // Boot avec session restaurée (Capacitor iOS / Safari mobile partitioned) : nativeSessionManager publie le token au boot
  // alors que des queries Apollo sont déjà en vol (UserQuery, WebsiteQuery). Sans cette attente, resetStore() vide le
  // store pendant que les queries résolvent leur réponse → Invariant Violation #3 → hang user ~1 min.
  // Cf. ticket apollo-store-reset-race_P1/B0_01.
  useEffect(() => {
    return subscribeToToken(async (token, prevToken) => {
      if ((prevToken && !token) || (!prevToken && token)) {
        // Boucle while + timeout 2 s :
        // - capture les queries lancées par les subscribers ultérieurs du forEach (login.tsx Feb 2026 → forceRefetchUser) ;
        // - filtre par `networkStatus` pour ne garder que les queries avec un fetch réseau en vol (sinon
        //   ObservableQuery.result() résoudrait immédiatement avec la valeur cachée → boucle serrée jusqu'au timeout) ;
        // - garde-fou timeout 2 s contre les queries suspendues (polling, retry) qui ne résoudraient jamais .result() ;
        // - cas concurrent logout → relogin rapide non géré ici (deux callbacks async chevauchent → deux resetStore
        //   séquentiels, non cassant).
        const deadline = Date.now() + 2000;
        while (Date.now() < deadline) {
          const active = apolloClient.getObservableQueries('active');
          // NetworkStatus : 1=loading, 2=setVariables, 3=fetchMore, 4=refetch (= fetch réseau en vol).
          // Exclut 6=poll (pattern auto-retry, n'attendrait jamais), 7=ready, 8=error.
          const fetching = [...active.values()].filter(oq => {
            const status = oq.getCurrentResult().networkStatus;
            return status !== undefined && status >= 1 && status <= 4;
          });
          if (fetching.length === 0) break;
          await Promise.race([
            Promise.allSettled(fetching.map(oq => oq.result())),
            new Promise(resolve => setTimeout(resolve, Math.max(0, deadline - Date.now()))),
          ]);
        }
        apolloClient.resetStore();
      }
    });
  }, [subscribeToToken, apolloClient]);

  useEffect(
    () => gcAuthInterceptor({ getPublicKey, getToken, triggerRefreshToken }),
    [getPublicKey, getToken, logout, triggerRefreshToken],
  );

  return { apolloClient, auth, getToken };
}
